import { useEffect, useRef, useState } from 'react';
import { globalEventBus, EVENTS } from '../services/eventBus';
import type { GraphData } from '../types';

export function KnowledgeGraphPanel() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<GraphData>({ nodes: [], links: [] });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const selectedNodeIdRef = useRef<string | null>(null);

  useEffect(() => {
    const unsub = globalEventBus.subscribe(EVENTS.GRAPH_UPDATE, (payload) => {
      setData((prev) => {
        const next = payload as GraphData;
        return {
          nodes: next.nodes.length > 0 ? next.nodes : prev.nodes,
          links: next.links,
        };
      });
    });
    return unsub;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return;
    }

    const resize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = Math.max(1, rect.width);
      canvas.height = Math.max(1, rect.height);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;
      const currentZoom = zoomRef.current;
      const currentOffset = offsetRef.current;
      const worldX = (cursorX - currentOffset.x) / currentZoom;
      const worldY = (cursorY - currentOffset.y) / currentZoom;

      const delta = event.deltaY > 0 ? 0.9 : 1.1;
      const nextZoom = Math.min(2.5, Math.max(0.4, currentZoom * delta));
      if (!Number.isFinite(nextZoom)) {
        return;
      }

      const nextOffset = {
        x: cursorX - worldX * nextZoom,
        y: cursorY - worldY * nextZoom,
      };
      zoomRef.current = nextZoom;
      offsetRef.current = nextOffset;
      setZoom(nextZoom);
      setOffset(nextOffset);
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleClick = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;

      const currentZoom = zoomRef.current;
      const currentOffset = offsetRef.current;
      const worldX = (clickX - currentOffset.x) / currentZoom;
      const worldY = (clickY - currentOffset.y) / currentZoom;

      // Find clicked node (hexagon or circle hit area)
      for (const node of data.nodes) {
        const nodeX = node.x ?? 0;
        const nodeY = node.y ?? 0;
        const isHex = node.group === 'core' || node.group === 'customer';
        const baseSize = node.val ? Math.max(6, node.val / 2) : 8;
        const hitRadius = isHex ? Math.max(14, baseSize + 8) + 4 : (node.val ? Math.max(3, node.val / 2) : 5) + 5;
        const distance = Math.sqrt(
          Math.pow(worldX - nodeX, 2) + Math.pow(worldY - nodeY, 2)
        );

        if (distance <= hitRadius) {
          setSelectedNodeId(node.id);
          globalEventBus.emit(EVENTS.NODE_SELECTED, node);
          return;
        }
      }
    };

    canvas.addEventListener('click', handleClick);
    return () => canvas.removeEventListener('click', handleClick);
  }, [data.nodes]);

  const fitToView = () => {
    const canvas = canvasRef.current;
    if (!canvas || data.nodes.length === 0) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      return;
    }

    const xs = data.nodes.map((node) => node.x ?? canvas.width / 2);
    const ys = data.nodes.map((node) => node.y ?? canvas.height / 2);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const padding = 80;
    const width = Math.max(1, maxX - minX + padding * 2);
    const height = Math.max(1, maxY - minY + padding * 2);
    const scale = Math.min(canvas.width / width, canvas.height / height, 2.2);
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const nextZoom = Math.max(0.4, Math.min(2.2, scale));
    setZoom(nextZoom);
    setOffset({
      x: canvas.width / 2 - centerX * nextZoom,
      y: canvas.height / 2 - centerY * nextZoom,
    });
  };

  useEffect(() => {
    if (data.nodes.length > 0) {
      fitToView();
    }
  }, [data.nodes.length]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let cachedTheme: { bgColor: string; gridColor: string } | null = null;
    let cachedIsDark: boolean | null = null;

    const nodes = data.nodes.map((node) => ({
      ...node,
      x: node.x ?? canvas.width / 2 + (Math.random() - 0.5) * 100,
      y: node.y ?? canvas.height / 2 + (Math.random() - 0.5) * 100,
      vx: node.vx ?? 0,
      vy: node.vy ?? 0,
    }));

    const render = () => {
      const isDark = document.documentElement.classList.contains('dark');

      if (cachedTheme === null || cachedIsDark !== isDark) {
        const rootStyles = getComputedStyle(document.documentElement);
        const bgVar = rootStyles.getPropertyValue('--graph-bg').trim();
        const gridVar = rootStyles.getPropertyValue('--graph-grid').trim();

        cachedTheme = {
          bgColor: bgVar ? `hsl(${bgVar})` : (isDark ? '#050a14' : '#f1f5f9'),
          gridColor: gridVar ? `hsl(${gridVar})` : (isDark ? 'rgba(0,180,255,0.04)' : 'rgba(0,120,200,0.08)'),
        };
        cachedIsDark = isDark;
      }

      // ── SOPHO GRAPH Sci-Fi palette ──
      const bgColor = cachedTheme?.bgColor ?? (isDark ? '#050a14' : '#f1f5f9');
      const gridColor = cachedTheme?.gridColor ?? (isDark ? 'rgba(0,180,255,0.04)' : 'rgba(0,120,200,0.08)');

      // Glow color per group
      const groupColors: Record<string, { glow: string; fill: string; label: string }> = {
        core:     { glow: '#00D4FF', fill: 'rgba(0, 20, 50, 0.85)',   label: '#ccfbf1' },
        customer: { glow: '#00E5A0', fill: 'rgba(0, 30, 30, 0.85)',   label: '#d1fae5' },
        draft:    { glow: '#FFB020', fill: 'rgba(30, 20, 0, 0.85)',   label: '#fef3c7' },
        unknown:  { glow: '#FF4060', fill: 'rgba(30, 10, 10, 0.85)',  label: '#fecaca' },
      };
      const defaultColors = { glow: '#00D4FF', fill: 'rgba(0, 15, 40, 0.85)', label: '#ccfbf1' };

      const currentZoom = zoomRef.current;
      const currentOffset = offsetRef.current;
      const worldCenterX = (canvas.width / 2 - currentOffset.x) / currentZoom;
      const worldCenterY = (canvas.height / 2 - currentOffset.y) / currentZoom;

      // ── Physics simulation ──
      nodes.forEach((node) => {
        nodes.forEach((other) => {
          if (node === other) return;
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 45 / (dist * dist);
          node.vx += (dx / dist) * force;
          node.vy += (dy / dist) * force;
        });

        const centerDx = worldCenterX - node.x;
        const centerDy = worldCenterY - node.y;
        node.vx += centerDx * 0.0006;
        node.vy += centerDy * 0.0006;

        node.vx *= 0.88;
        node.vy *= 0.88;
        node.x += node.vx;
        node.y += node.vy;
      });

      // ── Clear & background ──
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // ── Subtle grid overlay ──
      const gridSpacing = 40;
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;
      for (let gx = 0; gx < canvas.width; gx += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(gx, 0);
        ctx.lineTo(gx, canvas.height);
        ctx.stroke();
      }
      for (let gy = 0; gy < canvas.height; gy += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, gy);
        ctx.lineTo(canvas.width, gy);
        ctx.stroke();
      }

      ctx.setTransform(currentZoom, 0, 0, currentZoom, currentOffset.x, currentOffset.y);

      // ── Draw edges with subtle glow ──
      data.links.forEach((link) => {
        const source = nodes.find((n) => n.id === link.source);
        const target = nodes.find((n) => n.id === link.target);
        if (!source || !target) return;

        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        ctx.strokeStyle = 'rgba(0, 180, 255, 0.15)';
        ctx.lineWidth = 1.5;
        ctx.shadowColor = '#00D4FF';
        ctx.shadowBlur = 4;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Edge label
        if (link.label) {
          const midX = (source.x + target.x) / 2;
          const midY = (source.y + target.y) / 2;
          ctx.font = "7px 'Courier New', monospace";
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'rgba(0, 180, 255, 0.5)';
          ctx.fillText(link.label, midX, midY - 4);
          ctx.textAlign = 'start';
          ctx.textBaseline = 'alphabetic';
        }
      });

      // ── Hexagon path helper ──
      const drawHexagon = (x: number, y: number, radius: number) => {
        ctx.beginPath();
        for (let i = 0; i < 6; i += 1) {
          // Start at flat-top orientation (offset by -π/6)
          const angle = (Math.PI * 2 * i) / 6 - Math.PI / 6;
          const px = x + radius * Math.cos(angle);
          const py = y + radius * Math.sin(angle);
          if (i === 0) {
            ctx.moveTo(px, py);
          } else {
            ctx.lineTo(px, py);
          }
        }
        ctx.closePath();
      };

      // ── Draw nodes ──
      nodes.forEach((node) => {
        const palette = groupColors[node.group] ?? defaultColors;
        const isSelected = selectedNodeIdRef.current === node.id;
        const isHexNode = node.group === 'core' || node.group === 'customer';

        if (isHexNode) {
          // ── HEXAGON rendering for core & customer ──
          const baseSize = node.val ? Math.max(6, node.val / 2) : 8;
          const hexRadius = Math.max(14, baseSize + 8);

          // Glow layer (intensified when selected)
          ctx.shadowColor = palette.glow;
          ctx.shadowBlur = isSelected ? 45 : 15;

          // Fill: brighter when selected
          drawHexagon(node.x, node.y, hexRadius);
          ctx.fillStyle = isSelected ? palette.glow : palette.fill;
          ctx.globalAlpha = isSelected ? 0.35 : 1;
          ctx.fill();
          ctx.globalAlpha = 1;

          // Border: standard neon glow
          drawHexagon(node.x, node.y, hexRadius);
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = palette.glow;
          ctx.stroke();

          ctx.shadowBlur = 0;

          // Inner tech detail: horizontal scanner line
          ctx.beginPath();
          const halfW = hexRadius * 0.5;
          ctx.moveTo(node.x - halfW, node.y);
          ctx.lineTo(node.x + halfW, node.y);
          ctx.strokeStyle = palette.glow;
          ctx.globalAlpha = 0.2;
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.globalAlpha = 1;

          // Small corner accents
          const accentSize = hexRadius * 0.15;
          ctx.strokeStyle = palette.glow;
          ctx.lineWidth = 0.8;
          ctx.globalAlpha = 0.35;

          const tlX = node.x - hexRadius * 0.55;
          const tlY = node.y - hexRadius * 0.55;
          ctx.beginPath();
          ctx.moveTo(tlX, tlY + accentSize);
          ctx.lineTo(tlX, tlY);
          ctx.lineTo(tlX + accentSize, tlY);
          ctx.stroke();

          const brX = node.x + hexRadius * 0.55;
          const brY = node.y + hexRadius * 0.55;
          ctx.beginPath();
          ctx.moveTo(brX, brY - accentSize);
          ctx.lineTo(brX, brY);
          ctx.lineTo(brX - accentSize, brY);
          ctx.stroke();

          ctx.globalAlpha = 1;

          // Label INSIDE hexagon (abbreviated)
          const fontSize = isSelected ? 9 : 8;
          ctx.font = `bold ${fontSize}px 'Courier New', monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = palette.label;

          const maxChars = Math.floor(hexRadius / 3.5);
          const displayLabel = node.label.length > maxChars
            ? node.label.substring(0, maxChars - 1) + '…'
            : node.label;
          ctx.fillText(displayLabel, node.x, node.y - 2);

          // Tiny group tag
          ctx.globalAlpha = 0.5;
          ctx.font = `${5}px 'Courier New', monospace`;
          ctx.fillStyle = palette.glow;
          ctx.fillText(node.group.toUpperCase(), node.x, node.y + hexRadius * 0.45);
          ctx.globalAlpha = 1;

          // Full label BELOW hexagon
          ctx.font = isSelected
            ? "bold 9px 'Courier New', monospace"
            : "8px 'Courier New', monospace";
          ctx.fillStyle = palette.label;
          ctx.globalAlpha = isSelected ? 1 : 0.75;
          ctx.fillText(node.label, node.x, node.y + hexRadius + 10);
          ctx.globalAlpha = 1;

          ctx.textAlign = 'start';
          ctx.textBaseline = 'alphabetic';
        } else {
          // ── CIRCLE rendering for draft & unknown ──
          const size = node.val ? Math.max(3, node.val / 2) : 5;

          // Filled circle with glow (intensified when selected)
          ctx.beginPath();
          ctx.arc(node.x, node.y, isSelected ? size + 2 : size, 0, Math.PI * 2);
          ctx.fillStyle = palette.glow;
          ctx.shadowColor = palette.glow;
          ctx.shadowBlur = isSelected ? 35 : 10;
          ctx.fill();
          ctx.shadowBlur = 0;

          // Label next to the circle
          ctx.fillStyle = palette.label;
          ctx.font = isSelected ? "bold 10px 'Courier New', monospace" : "9px 'Courier New', monospace";
          ctx.fillText(node.label, node.x + size + 5, node.y + 3);
        }
      });

      nodes.forEach((node, index) => {
        if (data.nodes[index]) {
          data.nodes[index].x = node.x;
          data.nodes[index].y = node.y;
          data.nodes[index].vx = node.vx;
          data.nodes[index].vy = node.vy;
        }
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animationFrameId);
  }, [data, selectedNodeId]);

  return (
    <div
      ref={containerRef}
      className="h-full w-full bg-[hsl(var(--graph-bg))] relative flex flex-col"
    >
      <div className="p-3 border-b border-[hsl(var(--graph-panel-border))]">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-mono">
          Knowledge Graph
        </h3>
      </div>
      <div className="relative flex-1">
        <canvas ref={canvasRef} className="h-full w-full cursor-pointer" />
        <div className="absolute top-2 right-2 flex items-center gap-2 bg-[hsl(var(--graph-panel)/0.9)] border border-[hsl(var(--graph-panel-border))] rounded px-2 py-1 text-[10px] text-[hsl(var(--graph-panel-muted))] font-mono">
          <button
            type="button"
            onClick={() => setZoom((current) => {
              const canvas = canvasRef.current;
              if (!canvas) {
                return current;
              }
              const next = Math.min(2.5, current + 0.1);
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;
              const offsetState = offsetRef.current;
              const nextOffset = {
                x: centerX - (centerX - offsetState.x) * (next / current),
                y: centerY - (centerY - offsetState.y) * (next / current),
              };
              zoomRef.current = next;
              offsetRef.current = nextOffset;
              setOffset(nextOffset);
              return next;
            })}
            className="px-1.5 py-0.5 rounded border border-[hsl(var(--graph-panel-border))] hover:bg-[hsl(var(--primary)/0.12)] hover:text-[hsl(var(--primary))] transition-colors"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => setZoom((current) => {
              const canvas = canvasRef.current;
              if (!canvas) {
                return current;
              }
              const next = Math.max(0.4, current - 0.1);
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;
              const offsetState = offsetRef.current;
              const nextOffset = {
                x: centerX - (centerX - offsetState.x) * (next / current),
                y: centerY - (centerY - offsetState.y) * (next / current),
              };
              zoomRef.current = next;
              offsetRef.current = nextOffset;
              setOffset(nextOffset);
              return next;
            })}
            className="px-1.5 py-0.5 rounded border border-[hsl(var(--graph-panel-border))] hover:bg-[hsl(var(--primary)/0.12)] hover:text-[hsl(var(--primary))] transition-colors"
          >
            -
          </button>
          <button
            type="button"
            onClick={fitToView}
            className="px-1.5 py-0.5 rounded border border-[hsl(var(--graph-panel-border))] hover:bg-[hsl(var(--primary)/0.12)] hover:text-[hsl(var(--primary))] transition-colors"
          >
            Fit
          </button>
        </div>
        <div className="absolute top-2 left-2 bg-[hsl(var(--graph-panel)/0.9)] p-2 rounded text-[10px] text-[hsl(var(--graph-panel-muted))] border border-[hsl(var(--graph-panel-border))] font-mono">
          <h4 className="font-bold text-[hsl(var(--graph-panel-text))] mb-1 tracking-wider uppercase text-[9px]">Ontology Legend</h4>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rotate-45 bg-[#00D4FF] shadow-[0_0_4px_#00D4FF]"></div>
              <span>Core (Global)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rotate-45 bg-[#00E5A0] shadow-[0_0_4px_#00E5A0]"></div>
              <span>Customer (Approved)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rotate-45 bg-[#FFB020] shadow-[0_0_4px_#FFB020]"></div>
              <span>Draft (User)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rotate-45 bg-[#FF4060] shadow-[0_0_4px_#FF4060]"></div>
              <span>Unknown</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
